/**
 * settings-general-route.js — client-saas /settings/general
 *
 * The signed-in user's PERSONAL tab: their own name/company/phone, their own
 * device preferences, and a doorway to their own sign-in security. Like
 * Security (and unlike Notifications/Integrations/Workspace) nothing here is
 * org-scoped, so there is deliberately no portfolio picker: a reseller sitting
 * on this page is editing their OWN profile, not a client's.
 *
 * The profile pair is GET/PUT /m5t/v5/acctEntity/profile — both derive the
 * target account from the bearer token, so no guid is ever sent from here.
 * Theme + language are device-local (localStorage via core/theme.js and
 * I18nController), never persisted to the account.
 */

import AuthController from '../services/AuthController.js';
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { FEATURE_FLAGS } from '../../../../shared/featureFlags.js';
import { currentTheme, setTheme } from '../core/theme.js';
import { darkModeEnabled } from '../core/uiFlagGates.js';
import { I18nController } from '../controllers/i18nController.js';
import { guardedFetch } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
const toast = (message, type = 'info') => { try { PubSub.publish(AppBroadcast.UI_TOAST, { message, type }); } catch { /* no-op */ } };
const apiBase = () => AuthController.getConfig?.().BASE_URL || '';

const PROFILE_URL = () => `${apiBase()}/m5t/v5/acctEntity/profile`;

const state = { profile: null };

let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-settings-general-styles';
  // Layout only, and only for the two things settings-kit.css leaves open:
  // the link card carries no padding of its own (it is a bare .dashboard-card),
  // and .settings-select is width:100% which has no meaning inside the
  // shrink-to-fit .settings-item__control. No colors, no transitions.
  s.textContent = `
    .page-shell[data-section="settings-general"] a.settings-link-card { padding: 18px 24px; }
    .page-shell[data-section="settings-general"] .settings-item__control .settings-select { width: auto; min-width: 190px; }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

// ── appearance + language (device-local, resolved at render time) ────────────
function availableLangs() {
  try { return I18nController.getAvailableLanguages() || []; } catch { return []; }
}

function currentLang() {
  // A stored code that is no longer offered (a language was retired, or the
  // value predates a rename) must not win: the select would render with no
  // option selected, showing the first option while the app runs the stale
  // one — and choosing the displayed option would fire no change event.
  const langs = availableLangs();
  const fallback = langs.includes(FEATURE_FLAGS.FF_SSE_I18N_LANG)
    ? FEATURE_FLAGS.FF_SSE_I18N_LANG
    : (langs[0] || FEATURE_FLAGS.FF_SSE_I18N_LANG);
  try {
    const stored = localStorage.getItem(FEATURE_FLAGS.FF_SSE_LS_USER_LANG);
    return stored && langs.includes(stored) ? stored : fallback;
  } catch { return fallback; }
}

/** Best-effort native language name, falling back to the raw code. */
function langLabel(code) {
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code);
    if (name && name !== code) return name.charAt(0).toUpperCase() + name.slice(1);
  } catch { /* fall through to the code */ }
  return code;
}

function appearanceCardHTML() {
  const showDark = darkModeEnabled();
  const langs = availableLangs();
  const showLang = langs.length > 1;
  if (!showDark && !showLang) return '';

  const lang = currentLang();
  return `
    <div class="dashboard-card" id="gen-appearance-card" style="margin-top:24px">
      <div class="card-body">
        <div class="settings-section__head">
          <span class="settings-section__icon"><span class="material-symbols-outlined" aria-hidden="true">palette</span></span>
          <div>
            <h2 class="settings-section__title">Appearance and language</h2>
            <p class="settings-section__desc">Theme and display language.</p>
          </div>
        </div>
        ${showDark ? `
        <div class="settings-item" id="gen-dark-item">
          <div class="settings-item__info">
            <div class="settings-item__title">Dark mode</div>
            <div class="settings-item__desc">Off by default. Applies on this device.</div>
          </div>
          <div class="settings-item__control">
            <label class="m5t-switch">
              <input type="checkbox" role="switch" id="gen-dark" aria-label="Dark mode" ${currentTheme() === 'dark' ? 'checked' : ''} />
              <span class="m5t-switch__track" aria-hidden="true"></span>
            </label>
          </div>
        </div>` : ''}
        ${showLang ? `
        <div class="settings-item" id="gen-lang-item">
          <div class="settings-item__info">
            <div class="settings-item__title">Language</div>
          </div>
          <div class="settings-item__control">
            <select class="settings-select" id="gen-lang" aria-label="Language">
              ${langs.map((c) => `<option value="${esc(c)}" ${c === lang ? 'selected' : ''}>${esc(langLabel(c))}</option>`).join('')}
            </select>
          </div>
        </div>` : ''}
      </div>
    </div>`;
}

export function render() {
  ensureStyles();
  return `
    <div class="page-shell" data-section="settings-general">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">General</h1>
          <p class="page-description">Your profile and preferences.</p>
        </div>
      </div>

      <div class="dashboard-card">
        <div class="card-body">
          <div class="settings-section__head">
            <span class="settings-section__icon"><span class="material-symbols-outlined" aria-hidden="true">person</span></span>
            <div>
              <h2 class="settings-section__title">Profile</h2>
              <p class="settings-section__desc">How you appear across Mach Five Magnet.</p>
            </div>
          </div>
          <div id="gen-profile-body">${emptyState('hourglass_empty', 'Loading…')}</div>
        </div>
      </div>

      ${appearanceCardHTML()}

      <a class="dashboard-card settings-link-card" href="#/settings/security" style="margin-top:24px">
        <span class="settings-section__icon"><span class="material-symbols-outlined" aria-hidden="true">passkey</span></span>
        <div>
          <div class="settings-section__title">Sign-in and security</div>
          <p class="settings-section__desc">Password and passkeys.</p>
        </div>
        <span class="settings-link-card__go material-symbols-outlined" aria-hidden="true">chevron_right</span>
      </a>
    </div>`;
}

export async function init() {
  wireAppearance();
  await loadProfile();
}

// ── profile ─────────────────────────────────────────────────────────────────
function profileFormHTML(p) {
  return `
    <div class="settings-grid-2">
      <div class="settings-field">
        <label class="settings-label" for="gen-first">First name</label>
        <input class="settings-input" id="gen-first" type="text" autocomplete="given-name" value="${esc(p.first)}" />
        <p class="settings-error" id="gen-first-error" role="alert" hidden></p>
      </div>
      <div class="settings-field">
        <label class="settings-label" for="gen-last">Last name</label>
        <input class="settings-input" id="gen-last" type="text" autocomplete="family-name" value="${esc(p.last)}" />
        <p class="settings-error" id="gen-last-error" role="alert" hidden></p>
      </div>
    </div>
    <div class="settings-field">
      <label class="settings-label" for="gen-company">Company</label>
      <input class="settings-input" id="gen-company" type="text" autocomplete="organization" value="${esc(p.company)}" />
    </div>
    <div class="settings-field">
      <label class="settings-label" for="gen-phone">Phone</label>
      <input class="settings-input" id="gen-phone" type="tel" autocomplete="tel" value="${esc(p.phone)}" />
    </div>
    <div class="settings-field">
      <label class="settings-label" for="gen-email">Sign-in email</label>
      <input class="settings-input" id="gen-email" type="email" value="${esc(p.email)}" readonly />
      <p class="settings-help">Your sign-in email. Contact support to change it.</p>
    </div>
    <div class="settings-save-row">
      <button class="btn btn-primary" id="gen-save" type="button">Save</button>
    </div>`;
}

async function loadProfile() {
  const body = document.getElementById('gen-profile-body');
  if (!body) return;
  try {
    const res = await guardedFetch(PROFILE_URL());
    const data = await res.json().catch(() => ({}));
    failIfNotOk(res, data);
    if (!data.ok || !data.data) throw new Error(data.error || 'Failed to load');
    state.profile = data.data;
  } catch (err) {
    console.warn('[settings-general] profile load failed:', err);
    body.innerHTML = isDenial(err) ? denialState() : emptyState('error', 'Could not load your profile. Please try again.');
    return;
  }
  body.innerHTML = profileFormHTML(state.profile);
  body.querySelector('#gen-save')?.addEventListener('click', saveProfile);
}

/** Show (or clear) the inline error under a name field. */
function fieldError(id, msg) {
  const el = document.getElementById(`${id}-error`);
  if (el) {
    el.textContent = msg || '';
    el.hidden = !msg;
  }
  const input = document.getElementById(id);
  if (input) input.setAttribute('aria-invalid', msg ? 'true' : 'false');
}

async function saveProfile() {
  const btn = document.getElementById('gen-save');
  const val = (id) => (document.getElementById(id)?.value ?? '').trim();
  // Empty strings are a legitimate value for Company and Phone: clearing one is
  // how you remove it, so those stay in the payload as ''. Names are different —
  // signup's own invariant rejects falsy first/last, and downstream acctGroup
  // name-building depends on them — so a blanked name blocks the save.
  const payload = { first: val('gen-first'), last: val('gen-last'), company: val('gen-company'), phone: val('gen-phone') };

  fieldError('gen-first', payload.first ? '' : 'First name is required.');
  fieldError('gen-last', payload.last ? '' : 'Last name is required.');
  if (!payload.first || !payload.last) return;

  if (btn) btn.disabled = true;
  try {
    const res = await guardedFetch(PROFILE_URL(), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) throw new Error(data.error || 'Could not save.');
    if (data.data) state.profile = data.data;
    toast('Profile updated.', 'success');
  } catch (err) {
    console.warn('[settings-general] profile save failed:', err);
    toast(err.message || 'Could not save.', 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

// ── device preferences ──────────────────────────────────────────────────────
function wireAppearance() {
  const dark = document.getElementById('gen-dark');
  if (dark) {
    dark.addEventListener('change', () => {
      setTheme(dark.checked ? 'dark' : 'light');
      // setTheme no-ops when the kill switch is off — keep the switch honest
      // rather than showing a state the app never entered.
      dark.checked = currentTheme() === 'dark';
    });
    // The theme can also change elsewhere (the nav-footer toggle publishes
    // UI_THEME_CHANGED via core/theme.js); resync so the switch never shows a
    // stale state — a stale switch is also a dead one, since setTheme
    // early-returns when asked for the theme already applied. Route modules
    // have no destroy hook, so the subscription cleans itself up: once THIS
    // switch has left the document, the page is gone and the token is retired.
    const token = PubSub.subscribe(AppBroadcast.UI_THEME_CHANGED, () => {
      if (!dark.isConnected) { PubSub.unsubscribe(token); return; }
      dark.checked = currentTheme() === 'dark';
    });
  }

  const lang = document.getElementById('gen-lang');
  if (lang) {
    lang.addEventListener('change', () => {
      try { I18nController.setLanguage(lang.value); } catch (err) { console.warn('[settings-general] setLanguage failed:', err); }
    });
  }
}

export default { render, init };
